/**
 * Banking Page
 *
 * Bank accounts, transactions, and reconciliation management.
 * Supports both manual bank accounts and Open Banking (TrueLayer) connections.
 */

import React, { useState } from 'react';
import type { BankAccount, BankTransaction } from '../../types/banking';
import { maskIBAN } from '../../types/banking';
import type { CentsAmount, OBAccount } from '@business-assistant/shared';
import { useHasBankConnections, useTenant } from '@business-assistant/shared';
import { BankAccountForm } from '../forms';
import type { BankAccountFormData } from '../forms';
import { FastActions, type ActionType, type LinkedCounts } from '../components/FastActions';
import {
  ConnectBankButton,
  OpenBankAccountsList,
  OpenBankTransactionsList,
} from '../components/openbanking';
import '../accounting.css';

// =============================================================================
// TYPES
// =============================================================================

interface BankingPageProps {
  onNavigate?: (path: string) => void;
  onBack?: () => void;
}

type ViewMode = 'openbanking' | 'accounts' | 'transactions' | 'reconciliation' | 'import';

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

function formatCurrency(cents: CentsAmount): string {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
  }).format(cents / 100);
}

function formatDate(date: string): string {
  return new Date(date).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

// NOTE: No mock data - data comes from database via API

// =============================================================================
// COMPONENTS
// =============================================================================

function BankAccountCard({
  account,
  onClick,
}: {
  account: BankAccount;
  onClick: () => void;
}) {
  const getAccountIcon = () => {
    switch (account.accountType) {
      case 'cash':
        return 'M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z';
      case 'credit':
        return 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z';
      default:
        return 'M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z';
    }
  };

  return (
    <div
      onClick={onClick}
      className="accounting-card--start"
    >
      {/* Icon */}
      <div className="accounting-icon--md accounting-flex--center">
        <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={getAccountIcon()} />
        </svg>
      </div>

      {/* Info */}
      <div className="accounting-content">
        <div className="accounting-flex--row accounting-flex--gap-sm">
          <span>{account.accountName}</span>
          {!account.isActive && (
            <span>Inattivo</span>
          )}
        </div>
        {account.bankName && (
          <div>{account.bankName}</div>
        )}
        {account.iban && (
          <div>{maskIBAN(account.iban)}</div>
        )}
      </div>

      {/* Balance */}
      <div className="accounting-content--right">
        <div>{formatCurrency(account.currentBalance)}</div>
        {account.currentBalance !== account.reconciledBalance && (
          <div>Riconciliato: {formatCurrency(account.reconciledBalance)}</div>
        )}
      </div>
    </div>
  );
}

interface TransactionRowProps {
  transaction: BankTransaction;
  /** Linked items count per action type */
  linkedCounts?: LinkedCounts;
  onClick?: (transaction: BankTransaction) => void;
  onActionClick?: (actionType: ActionType, transaction: BankTransaction) => void;
}

function TransactionRow({
  transaction,
  linkedCounts = {},
  onClick,
  onActionClick,
}: TransactionRowProps) {
  const isCredit = transaction.type === 'credit';

  // Available actions for bank transactions
  const availableActions: ActionType[] = transaction.isReconciled
    ? ['note', 'file']
    : ['invoice', 'note', 'file', 'task'];

  return (
    <div
      className="accounting-transaction"
      onClick={() => onClick?.(transaction)}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      {/* Direction indicator */}
      <div
        className="accounting-transaction__indicator"
        style={{ backgroundColor: isCredit ? '#16a34a' : '#dc2626' }}
      />

      {/* Info */}
      <div className="accounting-content" style={{ flex: 1 }}>
        <div style={{ fontWeight: 500 }}>{transaction.description}</div>
        <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
          {formatDate(transaction.transactionDate)}
          {transaction.reference && ` | Rif: ${transaction.reference}`}
        </div>
      </div>

      {/* Amount */}
      <div style={{
        fontWeight: 600,
        fontSize: '1rem',
        color: isCredit ? '#16a34a' : '#dc2626',
        marginRight: '1rem',
      }}>
        {isCredit ? '+' : '-'}{formatCurrency(transaction.amount)}
      </div>

      {/* Reconciliation status badge */}
      <div style={{ marginRight: '1rem' }}>
        {transaction.isReconciled ? (
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            padding: '2px 8px',
            borderRadius: '4px',
            fontSize: '0.75rem',
            fontWeight: 500,
            backgroundColor: '#dcfce7',
            color: '#16a34a',
          }}>
            <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Riconciliato
          </span>
        ) : (
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '2px 8px',
            borderRadius: '4px',
            fontSize: '0.75rem',
            fontWeight: 500,
            backgroundColor: '#fef3c7',
            color: '#92400e',
          }}>
            Da riconciliare
          </span>
        )}
      </div>

      {/* Fast Actions with counter badges */}
      <div onClick={(e) => e.stopPropagation()}>
        <FastActions
          entityId={transaction.id}
          entityName={transaction.description}
          actions={availableActions}
          linkedCounts={linkedCounts}
          onActionClick={(actionType) => onActionClick?.(actionType, transaction)}
        />
      </div>
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function BankingPage({ onNavigate, onBack }: BankingPageProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('openbanking');
  // TODO: Load from API when banking store is available
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [transactions] = useState<BankTransaction[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [showAccountForm, setShowAccountForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState<BankAccount | null>(null);
  const [selectedOBAccountId, setSelectedOBAccountId] = useState<string | null>(null);

  // Get tenant/account context for Open Banking
  const { accountId: tenantAccountId } = useTenant();
  const accountId = tenantAccountId || '';
  const { data: hasBankConnections = false } = useHasBankConnections(accountId, !!accountId);

  const selectedAccount = selectedAccountId ? accounts.find((a) => a.id === selectedAccountId) : null;
  const accountTransactions = selectedAccountId
    ? transactions.filter((t) => t.accountId === selectedAccountId)
    : transactions;

  const totalBalance = accounts.filter((a) => a.isActive).reduce((sum, a) => sum + a.currentBalance, 0);
  const unreconciledCount = transactions.filter((t) => !t.isReconciled).length;

  // Handle Open Banking account selection
  const handleOBAccountSelect = (account: OBAccount) => {
    setSelectedOBAccountId(account.id);
  };

  return (
    <div className="accounting-page accounting-page--narrow">
      {/* Summary Stats */}
      <div className="accounting-grid--stats">
        <div className="accounting-stat--wide">
          <div>Saldo totale</div>
          <div>{formatCurrency(totalBalance)}</div>
        </div>
        <div className="accounting-stat--wide">
          <div>Conti attivi</div>
          <div>{accounts.filter((a) => a.isActive).length}</div>
        </div>
        <div className="accounting-stat--wide">
          <div>Da riconciliare</div>
          <div>{unreconciledCount}</div>
        </div>
      </div>

      {/* View Tabs */}
      <div className="accounting-tabs">
        {[
          { id: 'openbanking', label: 'Open Banking', icon: '🏦' },
          { id: 'accounts', label: 'Conti Manuali' },
          { id: 'transactions', label: 'Transazioni' },
          { id: 'import', label: 'Importa' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setViewMode(tab.id as ViewMode)}
            style={{
              backgroundColor: viewMode === tab.id ? '#3b82f6' : 'transparent',
              color: viewMode === tab.id ? 'white' : '#6b7280',
              padding: '8px 16px',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 500,
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            {tab.icon && <span>{tab.icon}</span>}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Open Banking View */}
      {viewMode === 'openbanking' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Connect Bank Section */}
          {!selectedOBAccountId && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '16px',
                backgroundColor: 'white',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
              }}
            >
              <div>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>
                  Collega i tuoi conti bancari
                </h3>
                <p style={{ margin: '4px 0 0', fontSize: '14px', color: '#6b7280' }}>
                  Sincronizza automaticamente saldo e movimenti con Open Banking
                </p>
              </div>
              <ConnectBankButton
                onSuccess={() => {
                  // Refresh the page data
                  window.location.reload();
                }}
                onError={(error) => {
                  console.error('Bank connection error:', error);
                }}
                compact
              />
            </div>
          )}

          {/* Back button when viewing transactions */}
          {selectedOBAccountId && (
            <button
              onClick={() => setSelectedOBAccountId(null)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                backgroundColor: 'transparent',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                width: 'fit-content',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Torna ai conti
            </button>
          )}

          {/* Bank Accounts or Transactions */}
          {accountId && !selectedOBAccountId && (
            <OpenBankAccountsList
              accountId={accountId}
              onSelectAccount={handleOBAccountSelect}
              showSync
              showDisconnect
            />
          )}

          {accountId && selectedOBAccountId && (
            <OpenBankTransactionsList
              bankAccountId={selectedOBAccountId}
              showSearch
              showFilters
            />
          )}

          {!accountId && (
            <div
              style={{
                padding: '32px',
                textAlign: 'center',
                color: '#6b7280',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
              }}
            >
              <p>Account non configurato. Contatta l'amministratore.</p>
            </div>
          )}
        </div>
      )}

      {/* Accounts View */}
      {viewMode === 'accounts' && (
        <div className="accounting-list--gap-lg">
          {accounts.map((account) => (
            <BankAccountCard
              key={account.id}
              account={account}
              onClick={() => {
                setSelectedAccountId(account.id);
                setViewMode('transactions');
              }}
            />
          ))}
          <button
            onClick={() => {
              setEditingAccount(null);
              setShowAccountForm(true);
            }}
            className="accounting-flex--center accounting-flex--gap-sm"
          >
            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Aggiungi conto
          </button>
        </div>
      )}

      {/* Transactions View */}
      {viewMode === 'transactions' && (
        <div>
          {selectedAccount && (
            <div className="accounting-flex--between">
              <div>
                <span>{selectedAccount.accountName}</span>
                <span>{selectedAccount.bankName}</span>
              </div>
              <button onClick={() => setSelectedAccountId(null)}>
                Tutti i conti
              </button>
            </div>
          )}
          <div>
            {accountTransactions.length === 0 ? (
              <div className="accounting-empty">
                Nessuna transazione
              </div>
            ) : (
              accountTransactions.map((transaction) => {
                // TODO: Fetch linked counts from LinkManager when available
                const linkedCounts: LinkedCounts = {};

                return (
                  <TransactionRow
                    key={transaction.id}
                    transaction={transaction}
                    linkedCounts={linkedCounts}
                    onClick={(t) => {
                      console.log('Open transaction detail:', t.id);
                      // TODO: Open transaction detail modal/form
                    }}
                    onActionClick={(actionType, t) => {
                      console.log('Fast action clicked:', actionType, 'on transaction:', t.id);
                      // TODO: Open LinkedActionsPanel for this action type
                    }}
                  />
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Import View */}
      {viewMode === 'import' && (
        <div className="accounting-upload">
          <svg
            width="48"
            height="48"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
            />
          </svg>
          <h3>Importa estratto conto</h3>
          <p>Trascina un file CSV, OFX, QIF o MT940 oppure</p>
          <button>
            Seleziona file
          </button>
        </div>
      )}

      {/* Bank Account Form Modal */}
      {showAccountForm && (
        <BankAccountForm
          account={editingAccount ? {
            id: editingAccount.id,
            name: editingAccount.accountName,
            accountType: editingAccount.accountType,
            bankName: editingAccount.bankName || '',
            iban: editingAccount.iban || '',
            accountNumber: '',
            currency: editingAccount.currency,
            initialBalance: editingAccount.openingBalance,
            currentBalance: editingAccount.currentBalance as CentsAmount,
            isActive: editingAccount.isActive,
            createdAt: editingAccount.createdAt,
            updatedAt: editingAccount.updatedAt,
          } : undefined}
          onSave={(data: BankAccountFormData) => {
            if (editingAccount) {
              // Update existing account
              setAccounts(accounts.map((a) =>
                a.id === editingAccount.id
                  ? {
                      ...a,
                      accountName: data.name,
                      accountType: data.accountType,
                      bankName: data.bankName,
                      iban: data.iban,
                      currency: data.currency,
                      openingBalance: Math.round(data.initialBalance * 100),
                      updatedAt: new Date().toISOString(),
                    }
                  : a
              ));
            } else {
              // Create new account
              const newAccount: BankAccount = {
                id: `bnk-${Date.now()}`,
                accountName: data.name,
                accountType: data.accountType,
                bankName: data.bankName,
                iban: data.iban,
                currency: data.currency,
                openingBalance: Math.round(data.initialBalance * 100),
                currentBalance: Math.round(data.initialBalance * 100),
                reconciledBalance: Math.round(data.initialBalance * 100),
                isActive: true,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
              };
              setAccounts([...accounts, newAccount]);
            }
            setShowAccountForm(false);
            setEditingAccount(null);
          }}
          onCancel={() => {
            setShowAccountForm(false);
            setEditingAccount(null);
          }}
        />
      )}
    </div>
  );
}

export type { BankingPageProps };
